import React, { useState, useEffect } from "react";
import { base44 } from "@voult/api/base44Client";
import { Key, Plus, Trash2, Copy, Check, Loader2, Activity } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

function generateApiKey() {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let key = "vk_live_";
  for (let i = 0; i < 32; i++) key += chars[Math.floor(Math.random() * chars.length)];
  return key;
}

export default function ApiCredentials() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [permissions, setPermissions] = useState(["tokenize"]);
  const [creating, setCreating] = useState(false);
  const [latestKey, setLatestKey] = useState(null);
  const [copied, setCopied] = useState(null);

  const PERMS = ["tokenize", "charge", "read", "delete"];

  const load = () => {
    setLoading(true);
    base44.entities.IntegrationKey.list("-created_date", 100).then(setKeys).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const togglePerm = (p) => setPermissions((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    const fullKey = generateApiKey();
    const created = await base44.entities.IntegrationKey.create({
      name: newName.trim(),
      key_prefix: fullKey.slice(0, 14) + "…",
      key_hash: fullKey,
      status: "active",
      permissions,
    });
    await base44.entities.ActivityLog.create({ action: "key_created", entity_type: "IntegrationKey", entity_id: created.id, details: `API key "${newName}" created` });
    setLatestKey({ id: created.id, fullKey });
    setNewName("");
    setPermissions(["tokenize"]);
    setShowForm(false);
    setCreating(false);
    load();
  };

  const revoke = async (key) => {
    if (!confirm(`Revoke "${key.name}"? This cannot be undone.`)) return;
    await base44.entities.IntegrationKey.update(key.id, { status: "revoked" });
    await base44.entities.ActivityLog.create({ action: "key_revoked", entity_type: "IntegrationKey", entity_id: key.id, details: `API key "${key.name}" revoked` });
    load();
  };

  const copyKey = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1800);
  };

  const activeKeys = keys.filter((k) => k.status === "active");
  const revokedKeys = keys.filter((k) => k.status === "revoked");

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">API Credentials</h1>
          <p className="text-sm text-slate-500 mt-0.5">{activeKeys.length} active key{activeKeys.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition">
          <Plus className="h-4 w-4" /> Generate key
        </button>
      </div>

      {/* New key form */}
      {showForm && (
        <div className="mb-5 bg-white rounded-2xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">New API Key</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Key name</label>
              <input value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Production Backend"
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">Permissions</label>
              <div className="flex flex-wrap gap-2">
                {PERMS.map((p) => (
                  <button key={p} type="button" onClick={() => togglePerm(p)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium capitalize transition ${permissions.includes(p) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={create} disabled={creating || !newName.trim()}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Newly created key reveal */}
      {latestKey && (
        <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm font-semibold text-emerald-800 mb-2">🔑 Copy your key — it won't be shown again.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded-lg border border-emerald-200 bg-white px-3 py-2 font-mono text-xs text-slate-800">{latestKey.fullKey}</code>
            <button onClick={() => copyKey(latestKey.fullKey, latestKey.id)}
              className="flex-shrink-0 rounded-lg border border-emerald-200 bg-white p-2 text-emerald-700 hover:bg-emerald-100">
              {copied === latestKey.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <button onClick={() => setLatestKey(null)} className="mt-2 text-xs text-emerald-700 hover:underline">Dismiss</button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : keys.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <Key className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p>No API keys yet. Generate one to get started.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {activeKeys.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Active Keys</p>
              </div>
              <div className="divide-y divide-slate-100">
                {activeKeys.map((k) => (
                  <div key={k.id} className="flex items-start gap-4 px-5 py-4">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                      <Key className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{k.name}</p>
                      <p className="font-mono text-xs text-slate-500 mt-0.5">{k.key_prefix}</p>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(k.permissions || []).map((p) => (
                          <span key={p} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 capitalize">{p}</span>
                        ))}
                      </div>
                      {k.last_used
                        ? <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Activity className="h-3 w-3" /> Last used {formatDistanceToNow(new Date(k.last_used), { addSuffix: true })}</p>
                        : <p className="text-xs text-slate-400 mt-1">Never used</p>}
                      <p className="text-xs text-slate-400">Created {k.created_date ? format(new Date(k.created_date), "MMM d, yyyy") : "—"}</p>
                    </div>
                    <button onClick={() => revoke(k)}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 transition">
                      <Trash2 className="h-3.5 w-3.5" /> Revoke
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {revokedKeys.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden opacity-60">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Revoked Keys</p>
              </div>
              <div className="divide-y divide-slate-100">
                {revokedKeys.map((k) => (
                  <div key={k.id} className="flex items-center gap-4 px-5 py-4">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100">
                      <Key className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-500 line-through">{k.name}</p>
                      <p className="font-mono text-xs text-slate-400 mt-0.5">{k.key_prefix}</p>
                    </div>
                    <span className="text-[10px] font-semibold uppercase rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">Revoked</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}